import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Facebook,
  Mail,
  MapPin,
  Phone,
  Clock,
  Server,
  Shield,
  CloudUpload,
  MessageCircle,
  ArrowUp
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const goToService = (serviceId: string) => {
    navigate(`?service=${serviceId}#services`, { replace: true });
    setTimeout(() => {
      const element = document.getElementById('services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.footer
      className="bg-gradient-to-b from-[#4f837d] to-[#3a645f] text-white pt-16 pb-8 relative overflow-hidden shadow-lg"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={containerVariants}
    >
      {/* Éléments décoratifs subtils */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="w-6 h-6 bg-white/10 rounded-full absolute top-16 left-1/6 animate-pulse" />
        <div className="w-4 h-4 bg-white/10 rounded-full absolute bottom-24 right-1/5 animate-pulse delay-500" />
      </div>

      <div className="container mx-auto px-6 relative z-10 font-sans">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12"
          variants={containerVariants}
        >
          {/* Colonne 1 - Logo et description */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <span className="text-3xl font-extrabold tracking-tight text-white entreprise">
                TAKE OPORTUN-IT
              </span>
            </div>
            <p className="text-white/90 text-base leading-relaxed mb-6">{t('footer.description')}</p>
            <div className="flex space-x-4">
              <motion.button 
                onClick={() => openExternal('https://facebook.com/votre-page')}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                aria-label="Facebook"
                whileHover={{ rotate: 10 }}
              >
                <Facebook size={20} />
              </motion.button>
              
              <motion.button 
                onClick={() => openExternal('https://wa.me/22666390000')}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                aria-label="WhatsApp"
                whileHover={{ rotate: 10 }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </motion.button>
              
              <motion.button 
                onClick={() => window.location.href = 'mailto:Sales@takeoportunit.com'}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
                aria-label="Email"
                whileHover={{ rotate: 10 }}
              >
                <Mail size={20} />
              </motion.button>
            </div>
          </motion.div>

          {/* Colonne 2 - Solutions */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-semibold text-white mb-6 border-b border-white/20 pb-2">
              {t('footer.solutions')}
            </h3>
            <ul className="space-y-4">
              <li>
                <motion.button 
                  onClick={() => goToService('cybersecurity')} 
                  className="text-white/90 hover:text-white transition-colors flex items-center group w-full text-left text-base"
                  whileHover={{ x: 5 }}
                >
                  <Shield size={18} className="mr-3 text-white group-hover:text-white/70 transition-colors" />
                  {t('footer.solution_security')}
                </motion.button>
              </li>
              <li>
                <motion.button 
                  onClick={() => goToService('backup')} 
                  className="text-white/90 hover:text-white transition-colors flex items-center group w-full text-left text-base"
                  whileHover={{ x: 5 }}
                >
                  <Server size={18} className="mr-3 text-white group-hover:text-white/70 transition-colors" />
                  {t('footer.solution_system')}
                </motion.button>
              </li>
              <li>
                <motion.button 
                  onClick={() => goToService('cloud')} 
                  className="text-white/90 hover:text-white transition-colors flex items-center group w-full text-left text-base"
                  whileHover={{ x: 5 }}
                >
                  <CloudUpload size={18} className="mr-3 text-white group-hover:text-white/70 transition-colors" />
                  {t('footer.solution_backup')}
                </motion.button>
              </li>
              <li>
                <motion.button 
                  onClick={() => goToService('worldsim')} 
                  className="text-white/90 hover:text-white transition-colors flex items-center groupahar w-full text-left text-base"
                  whileHover={{ x: 5 }}
                >
                  <MessageCircle size={18} className="mr-3 text-white group-hover:text-white/70 transition-colors" />
                  {t('footer.solution_communication')}
                </motion.button>
              </li>
            </ul>
          </motion.div>

          {/* Colonne 3 - Contact */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-semibold text-white mb-6 border-b border-white/20 pb-2">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-4 text-base">
              <li className="flex items-start">
                <MapPin size={18} className="text-white mr-3 mt-1 flex-shrink-0" />
                <span className="text-white/90">{t('footer.address')}</span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="text-white mr-3 flex-shrink-0" />
                <motion.button 
                  onClick={() => window.location.href = 'tel:+22666390000'}
                  className="text-white/90 hover:text-white transition-colors text-left"
                  whileHover={{ x: 5 }}
                >
                  {t('footer.phone')}
                </motion.button>
              </li>
              <li className="flex items-center">
                <Mail size={18} className="text-white mr-3 flex-shrink-0" />
                <motion.button 
                  onClick={() => window.location.href = 'mailto:Sales@takeoportunit.com'}
                  className="text-white/90 hover:text-white transition-colors text-left"
                  whileHover={{ x: 5 }}
                >
                  {t('footer.email')}
                </motion.button>
              </li>
              <li className="flex items-center">
                <Clock size={18} className="text-white mr-3 flex-shrink-0" />
                <span className="text-white/90">{t('footer.hours')}</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Ligne de copyright */}
        <motion.div 
          variants={itemVariants} 
          className="border-t border-white/20 pt-8"
        >
          <div className="flex flex-col items-center text-sm space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
            <p className="text-white/70 mb-4 sm:mb-0 text-center">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link 
                to="/mentions-legales" 
                className="text-white/70 hover:text-white transition-colors"
              >
                {t('footer.legal')}
              </Link>
              <Link 
                to="/politique-confidentialite" 
                className="text-white/70 hover:text-white transition-colors"
              >
                {t('footer.privacy')}
              </Link>
              <Link 
                to="/conditions-utilisation" 
                className="text-white/70 hover:text-white transition-colors"
              >
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bouton Remonter en haut */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 flex items-center justify-center rounded-full z-50 bg-gradient-to-br  text-white shadow-lg hover:shadow-[0_0_15px_3px_rgba(42,95,122,0.5)] transition-all duration-300 transform hover:scale-110"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showScrollTop ? 1 : 0, scale: showScrollTop ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        aria-label={t('footer.scroll_to_top')}
      >
        <ArrowUp size={20} strokeWidth={2.5} />
      </motion.button>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.2); opacity: 0.4; }
        }
        .animate-pulse {
          animation: pulse 4s ease-in-out infinite;
        }
        .delay-500 {
          animation-delay: 0.5s;
        }
        .entreprise {
          font-family: 'Inter', sans-serif;
          background: linear-gradient(to right, #ffffff, #e0e0e0);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        @media (max-width: 485px) {
          .entreprise {
            font-size: 1.5rem;
          }
        }
        @media (max-width: 405px) {
          .entreprise {
            font-size: 1.3rem;
          }
        }
        @media (max-width: 360px) {
          .entreprise {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </motion.footer>
  );
};

export default Footer;